<template>
  <div class="backsearch-page">
    <!-- Volume Counter (from /quota API) -->
    <div class="volume-banner" v-if="!isBusy">
      <template v-if="quotaLoading">
        <span class="volume-label">
          <i class="fas fa-chart-bar"></i>
          {{ $t('istarshine.backsearch.quota_loading') }}…
        </span>
      </template>
      <template v-else-if="quotaFetchFailed">
        <span class="quota-warning">
          <i class="fas fa-exclamation-circle"></i>
          {{ $t('istarshine.backsearch.quota_load_error') }}
        </span>
      </template>
      <template v-else>
        <span class="volume-label">
          <i class="fas fa-chart-bar"></i>
          {{ $t('istarshine.backsearch.volume_remaining') }}:
        </span>
        <strong :class="{ 'quota-danger': backsearchRemaining <= 0 }">{{ numberFmt(backsearchRemaining) }}</strong>
        <span class="volume-detail">
          ({{ numberFmt(backsearchProcessed) }} {{ $t('istarshine.backsearch.volume_processed') }} /
          {{ numberFmt(backsearchLimit) }} {{ $t('istarshine.backsearch.volume_limit') }})
        </span>
        <span v-if="backsearchRemaining <= 0" class="quota-warning">
          <i class="fas fa-exclamation-circle"></i>
          {{ $t('istarshine.backsearch.quota_reached') }}
        </span>
      </template>
    </div>

    <!-- Messages -->
    <div v-if="errorMessage" class="alert alert-error">
      <i class="fas fa-times-circle"></i> {{ errorMessage }}
      <button class="alert-close" @click="errorMessage = ''">&times;</button>
    </div>
    <div v-if="successMessage" class="alert alert-success">
      <i class="fas fa-check-circle"></i> {{ successMessage }}
    </div>

    <!-- Loading -->
    <div v-if="isBusy" class="text-center py-3">
      <loader-inline />
    </div>

    <!-- Main layout: sidebar + list -->
    <div v-if="!isBusy" class="bs-layout">
      <!-- LEFT SIDEBAR -->
      <aside class="bs-sidebar">
        <div class="sidebar-list">
          <div class="sidebar-header">
            <h4 class="sidebar-title">{{ $t('istarshine.backsearch.title') }}</h4>
            <a class="action-button" :title="$t('istarshine.backsearch.new_request')" @click.prevent="onNewRequest">
              <i class="far fa-plus-circle"></i>
            </a>
          </div>

          <div v-if="filteredRequests.length === 0" class="sidebar-empty">
            {{ searchQuery ? 'No matching requests' : $t('istarshine.backsearch.no_requests') }}
          </div>

          <div class="sidebar-items">
            <div
              v-for="req in filteredRequests"
              :key="req.id"
              class="sidebar-item"
              :class="{ active: selectedRequest && selectedRequest.id === req.id }"
              @click="onSidebarSelect(req)"
            >
              <div class="sidebar-item-primary">
                <span class="sidebar-status-dot" :class="sidebarStatusClass(req)"></span>
                <div class="sidebar-item-query">{{ sidebarPrimaryLine(req) }}</div>
              </div>
              <div class="sidebar-item-secondary">{{ sidebarMetaLine(req) }}</div>
            </div>
          </div>
        </div>
      </aside>

      <!-- RIGHT MAIN -->
      <section class="bs-main">
        <div class="bs-main-panel">
          <div class="bs-main-search">
            <div class="search-bar-wrapper">
              <input
                v-model="searchQuery"
                type="text"
                :placeholder="$t('istarshine.backsearch.search_placeholder') || 'Search requests…'"
                class="search-bar-input"
              />
              <i v-if="!searchQuery" class="fas fa-search search-bar-icon"></i>
              <i v-else class="fas fa-times search-bar-icon search-bar-clear" @click="searchQuery = ''"></i>
            </div>
          </div>

          <div class="bs-main-results">
            <backsearch-list
              :requests="filteredRequests"
              :deploy-disabled="deployDisabledByQuota"
              :selected-id="selectedRequest ? selectedRequest.id : null"
              @edit="onEdit"
              @deploy="onDeployRequest"
              @delete="onDeleteRequest"
              @select="onRequestSelect"
            />
          </div>
        </div>
      </section>
    </div>

    <!-- Deploy confirmation -->
    <confirmation-modal
      v-if="showDeployConfirm"
      :headerText="$t('istarshine.backsearch.confirm_deploy_title')"
      :message="$t('istarshine.backsearch.confirm_deploy_message')"
      :showDeleteButton="false"
      @confirmAction="confirmDeploy"
      @cancelAction="showDeployConfirm = false"
    />

    <!-- Delete confirmation -->
    <confirmation-modal
      v-if="showDeleteConfirm"
      :headerText="$t('istarshine.backsearch.confirm_delete_title')"
      :message="$t('istarshine.backsearch.confirm_delete_message')"
      :showDeleteButton="true"
      @confirmAction="confirmDelete"
      @cancelAction="showDeleteConfirm = false"
    />
  </div>
</template>

<script>
import { mapState } from 'vuex';
import axios from 'axios';

import BacksearchList from '../../components/istarshine/BacksearchList.vue';
import ConfirmationModal from '../../components/confirmation-modal.vue';
import loaderInline from '../../components/loader-inline.vue';
const BACKSEARCH_PATH = 'searches/asian_socials_backsearch_requests';

function parseBacksearchQuotaBody(responseBody) {
  if (!responseBody || typeof responseBody !== 'object') return null;
  const raw = responseBody.data && typeof responseBody.data === 'object'
    ? responseBody.data
    : responseBody;
  if (raw === null || raw === undefined || raw.quota === undefined) return null;
  return {
    quota: Number(raw.quota),
    used: Number(raw.used),
    remaining: Number(raw.remaining)
  };
}

const POLL_INTERVAL = 15000;
const numberFormat = new Intl.NumberFormat('en');

export default {
  name: 'backsearch-view',

  components: {
    BacksearchList,
    ConfirmationModal,
    loaderInline
  },

  data() {
    return {
      requests: [],
      isBusy: true,
      selectedRequest: null,
      errorMessage: '',
      successMessage: '',
      showDeployConfirm: false,
      pendingDeploy: null,
      showDeleteConfirm: false,
      pendingDelete: null,
      pollTimer: null,
      searchQuery: '',
      quotaSnapshot: null,
      quotaLoading: true,
      quotaFetchFailed: false
    };
  },

  computed: {
    ...mapState({
      project_id: state => state.booleanSearch.project_id
    }),

    backsearchLimit() {
      return this.quotaSnapshot ? this.quotaSnapshot.quota : 0;
    },

    backsearchProcessed() {
      return this.quotaSnapshot ? this.quotaSnapshot.used : 0;
    },

    backsearchRemaining() {
      return this.quotaSnapshot ? Math.max(0, this.quotaSnapshot.remaining) : 0;
    },

    /** While quota is loading, block deploy; if quota failed to load, allow deploy (server validates). */
    deployDisabledByQuota() {
      if (this.quotaLoading) return true;
      if (!this.quotaSnapshot) return false;
      return this.quotaSnapshot.remaining <= 0;
    },

    filteredRequests() {
      if (!this.searchQuery) return this.requests;
      const q = this.searchQuery.toLowerCase().trim();
      return this.requests.filter(r =>
        (r.query || '').toLowerCase().includes(q) ||
        (r.name || '').toLowerCase().includes(q) ||
        (r.status || '').toLowerCase().includes(q)
      );
    }
  },

  created() {
    this.fetchRequests();
    this.fetchQuota();
  },

  beforeDestroy() {
    this.stopPolling();
  },

  methods: {

    numberFmt(n) {
      return n !== null && n !== undefined ? numberFormat.format(n) : '0';
    },

    statusLabel(status) {
      if (!status) return this.$t('istarshine.backsearch.status_null');
      return this.$t('istarshine.backsearch.status_' + status);
    },

    formatDate(d) {
      if (!d) return '';
      return String(d).substring(0, 10);
    },

    sidebarPrimaryLine(req) {
      const n = req.name !== null && req.name !== undefined && String(req.name).trim();
      if (n) return n;
      return req.query || '';
    },

    normalizedStatus(status) {
      return status || 'draft';
    },

    sidebarStatusClass(req) {
      return `is-${this.normalizedStatus(req.status)}`;
    },

    sidebarMetaLine(req) {
      const completedDate = req.completed_at || req.updated_at || req.created_at;
      if (this.normalizedStatus(req.status) === 'completed' && completedDate) {
        return this.$t('istarshine.backsearch.completed_on', { date: this.formatDate(completedDate) });
      }

      const createdDate = req.created_at || req.updated_at;
      if (createdDate) {
        return this.$t('istarshine.backsearch.created_on', { date: this.formatDate(createdDate) });
      }

      return this.statusLabel(req.status);
    },

    isEditableRequest(req) {
      return this.normalizedStatus(req.status) === 'draft';
    },

    isReadonlyRequest(req) {
      return !!req && !this.isEditableRequest(req);
    },

    basePath() {
      return `projects/${this.project_id}/${BACKSEARCH_PATH}`;
    },

    async fetchQuota() {
      this.quotaLoading = true;
      this.quotaFetchFailed = false;
      try {
        const { data } = await axios.get(`projects/${this.project_id}/${BACKSEARCH_PATH}/quota`);
        const row = parseBacksearchQuotaBody(data);
        this.quotaSnapshot = row;
        if (!row) this.quotaFetchFailed = true;
      } catch (err) {
        console.error('Error fetching backsearch quota:', err);
        this.quotaSnapshot = null;
        this.quotaFetchFailed = true;
      } finally {
        this.quotaLoading = false;
      }
    },

    onRequestSelect(req) {
      this.selectedRequest = req;
      this.onEdit(req);
    },

    onSidebarSelect(req) {
      this.selectedRequest = req;
      this.onEdit(req);
    },

    async fetchRequests() {
      this.isBusy = true;
      try {
        const { data } = await axios.get(this.basePath());
        this.requests = Array.isArray(data.data) ? data.data : [];
        if (this.selectedRequest) {
          const fresh = this.requests.find(r => r.id === this.selectedRequest.id);
          this.selectedRequest = fresh || null;
        }
        this.startPollingIfNeeded();
      } catch (err) {
        console.error('Error fetching backsearch requests:', err);
        this.requests = [];
      } finally {
        this.isBusy = false;
      }
    },

    startPollingIfNeeded() {
      this.stopPolling();
      const hasPending = this.requests.some(r => r.status === 'queued' || r.status === 'processing');
      if (hasPending) {
        this.pollTimer = setInterval(() => this.pollRequests(), POLL_INTERVAL);
      }
    },

    stopPolling() {
      if (this.pollTimer) {
        clearInterval(this.pollTimer);
        this.pollTimer = null;
      }
    },

    async pollRequests() {
      try {
        const { data } = await axios.get(this.basePath());
        this.requests = Array.isArray(data.data) ? data.data : [];
        if (this.selectedRequest) {
          const fresh = this.requests.find(r => r.id === this.selectedRequest.id);
          this.selectedRequest = fresh || null;
        }
        const hasPending = this.requests.some(r => r.status === 'queued' || r.status === 'processing');
        if (!hasPending) this.stopPolling();
        this.fetchQuota();
      } catch (err) {
        console.error('Poll error:', err);
      }
    },

    onNewRequest() {
      this.$router.push({ name: 'clients--client_id-projects--project_id-istarshine-backsearch-create' });
    },

    onEdit(req) {
      this.$router.push({
        name: 'clients--client_id-projects--project_id-istarshine-backsearch-edit',
        params: { request_id: req.id }
      });
    },

    onDeployRequest(req) {
      if (this.quotaSnapshot && this.backsearchRemaining <= 0) {
        this.errorMessage = this.$t('istarshine.backsearch.quota_reached');
        return;
      }
      if (this.quotaSnapshot && req.max_post_count > this.backsearchRemaining) {
        this.errorMessage = this.$t('istarshine.backsearch.quota_exceeded');
        return;
      }
      this.pendingDeploy = req;
      this.showDeployConfirm = true;
    },

    async confirmDeploy() {
      this.showDeployConfirm = false;
      if (!this.pendingDeploy) return;
      this.clearMessages();
      try {
        await axios.post(`${this.basePath()}/${this.pendingDeploy.id}/deploy`);
        this.successMessage = this.$t('istarshine.backsearch.deploy_success');
        await this.fetchRequests();
        await this.fetchQuota();
        this.autoClearSuccess();
      } catch (err) {
        this.errorMessage = (err.response && err.response.data && err.response.data.error)
          || this.$t('istarshine.backsearch.deploy_error');
      } finally {
        this.pendingDeploy = null;
      }
    },

    onDeleteRequest(req) {
      this.pendingDelete = req;
      this.showDeleteConfirm = true;
    },

    async confirmDelete() {
      this.showDeleteConfirm = false;
      if (!this.pendingDelete) return;
      this.clearMessages();
      try {
        await axios.delete(`${this.basePath()}/${this.pendingDelete.id}`);
        this.successMessage = this.$t('istarshine.backsearch.delete_success');
        if (this.selectedRequest && this.selectedRequest.id === this.pendingDelete.id) {
          this.selectedRequest = null;
        }
        await this.fetchRequests();
        await this.fetchQuota();
        this.autoClearSuccess();
      } catch (err) {
        this.errorMessage = (err.response && err.response.data && err.response.data.error)
          || this.$t('istarshine.backsearch.delete_error');
      } finally {
        this.pendingDelete = null;
      }
    },

    clearMessages() {
      this.errorMessage = '';
      this.successMessage = '';
    },

    autoClearSuccess() {
      setTimeout(() => { this.successMessage = ''; }, 4000);
    }
  }
};
</script>

<style lang="scss" scoped>
@import "../../styles/base/variables.scss";

.backsearch-page {
  padding: 0 1rem;
  --bs-sticky-top: calc(60px + 3.5rem);
  --bs-sidebar-max-height: calc(100vh - var(--bs-sticky-top) - 1rem);
}

.volume-banner {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 0.35rem;
  padding: 0.75rem 1rem;
  background: $gray-100;
  border: 1px solid $gray-200;
  border-radius: 6px;
  margin-bottom: 1.25rem;
  font-size: 0.9rem;
}

.volume-label {
  font-weight: 500;
  i { margin-right: 0.3rem; color: $carma-teal; }
}

.volume-detail {
  color: $gray-500;
  font-size: 0.85rem;
}

.quota-danger { color: $red; }

.quota-warning {
  color: $red;
  font-size: 0.85rem;
  margin-left: 0.5rem;
  i { margin-right: 0.2rem; }
}

.alert {
  padding: 0.65rem 1rem;
  border-radius: 4px;
  margin-bottom: 1rem;
  font-size: 0.9rem;
  display: flex;
  align-items: center;
  gap: 0.35rem;
}

.alert-error {
  background: lighten($red, 42%);
  border: 1px solid lighten($red, 28%);
  color: darken($red, 10%);
}

.alert-success {
  background: lighten(#10b981, 48%);
  border: 1px solid lighten(#10b981, 32%);
  color: darken(#10b981, 25%);
}

.alert-close {
  margin-left: auto;
  background: none;
  border: none;
  font-size: 1.1rem;
  cursor: pointer;
  color: inherit;
}

/* ── Sidebar + Main flex layout (mirrors twitter-search-layout) ── */
.bs-layout {
  display: flex;
  align-items: flex-start;
  gap: 1rem;
}

.bs-sidebar {
  min-width: 17.15rem;
  width: 30%;
  flex: 0 0 30%;
  align-self: flex-start;
  height: var(--bs-sidebar-max-height);
  position: sticky;
  top: var(--bs-sticky-top);
  z-index: 1;
  overflow: hidden;
}

.bs-main {
  flex: 1 1 70%;
  max-width: 70%;
  min-width: 0;
  align-self: flex-start;
  height: var(--bs-sidebar-max-height);
  position: sticky;
  top: var(--bs-sticky-top);
  z-index: 1;
  overflow: hidden;
}

.bs-main-panel {
  border-radius: 6px;
  height: 100%;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.bs-main-results {
  flex: 1 1 auto;
  min-height: 0;
  overflow-y: auto;
  padding: 0.65rem 1rem 1rem;
  scrollbar-color: $gray-300 $gray-100;
  scrollbar-width: thin;

  :deep(.backsearch-list) {
    margin-bottom: 0;
  }

  &::-webkit-scrollbar { width: 6px; }
  &::-webkit-scrollbar-track { background: transparent; }
  &::-webkit-scrollbar-thumb { background: $gray-300; border-radius: 3px; }
  &::-webkit-scrollbar-thumb:hover { background: $gray-400; }
}

/* ── Sidebar list ── */
.sidebar-list {
  background: $white;
  border: 1px solid $gray-200;
  border-radius: 6px;
  padding: 0;
  display: flex;
  flex-direction: column;
  height: 100%;
  overflow: hidden;
}

.sidebar-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0.75rem 1rem;
  border-bottom: 1px solid $gray-200;
}

.sidebar-title {
  margin: 0;
  font-size: 0.95rem;
  font-weight: 600;
}

.action-button {
  cursor: pointer;
  i {
    color: $gray-500;
    font-size: 1.2rem;
    transition: color 0.25s ease-in;
  }
  &:hover i { color: $orange; }
}

/* Search row — class name must NOT be `search-section` (global boolean-search styles target that) */
.bs-main-search {
  flex: 0 0 auto;
  padding: 0.65rem 1rem;
}

.search-bar-wrapper {
  position: relative;
}

.search-bar-input {
  width: 100%;
  padding: 0.7rem 2rem 0.7rem 0.75rem;
  background-color: $white;
  border: 1px solid $gray-300;
  border-radius: $binary-radius;
  box-shadow: $box-shadow;
  color: $gray-600;
  font-size: 0.9rem;
  font-weight: 300;

  &::placeholder {
    font-style: italic;
    font-size: 0.9rem;
    font-weight: 300;
    color: $gray-400;
  }

  &:focus {
    outline: none;
    border-color: $carma-teal;
    background-color: $white;
  }
}

.search-bar-icon {
  position: absolute;
  right: 0.75rem;
  top: 50%;
  transform: translateY(-50%);
  color: $gray-400;
  font-size: 0.85rem;
  transition: color 0.25s ease-in;
}

.search-bar-clear {
  cursor: pointer;
  &:hover { color: $carma-teal; }
}

/* ── Sidebar items scrollable ── */
.sidebar-items {
  flex: 1 1 auto;
  min-height: 0;
  overflow-y: auto;
  scrollbar-color: $gray-300 $gray-100;
  scrollbar-width: thin;

  &::-webkit-scrollbar { width: 6px; }
  &::-webkit-scrollbar-track { background: transparent; }
  &::-webkit-scrollbar-thumb { background: $gray-300; border-radius: 3px; }
  &::-webkit-scrollbar-thumb:hover { background: $gray-400; }
}

.sidebar-empty {
  padding: 1.5rem 1rem;
  text-align: center;
  color: $gray-400;
  font-size: 0.875rem;
}

.sidebar-item {
  padding: 0.75rem 1rem;
  cursor: pointer;
  border-bottom: 1px solid $gray-100;
  transition: background 0.12s;

  &:last-child { border-bottom: none; }
  &:hover { background: lighten($gray-100, 2%); }
  &.active {
    background: $gray-100;
  }
}

.sidebar-item-primary {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin-bottom: 0.2rem;
}

.sidebar-status-dot {
  flex: 0 0 auto;
  width: 6px;
  height: 6px;
  border-radius: 50%;

  &.is-draft { background: $gray-300; }
  &.is-queued { background: #f59e0b; }
  &.is-processing { background: $carma-teal; }
  &.is-completed { background: #10b981; }
  &.is-errored { background: $red; }
}

.sidebar-item-query {
  font-size: 0.875rem;
  font-weight: 500;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  color: $gray-700;
}

.sidebar-item-secondary {
  font-size: 0.75rem;
  color: $gray-400;
}

.py-3 { padding: 1.5rem 0; }
</style>
